import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import PageContainer from './PageContainer';
import { email, gitlab } from '../../utils/generalData.jsx'
import { bracketLinkHoverStyle } from '../../styles/linkStyles';

const FooterWrapper = styled.footer`
    padding: 80px 0 40px;
    position: relative;
    border-top: 1px solid var(--grid-color);
    width: 100%; // Ensure footer spans full width
`;

const FooterContent = styled.div`
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 40px; // Add gap for spacing

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 30px;
    }
`;

const FooterColumn = styled.div`
    margin-bottom: 20px; /* Adjusted margin */
    flex: 1; /* Allow columns to grow */
    min-width: 180px; /* Optional: set a minimum width before wrapping */

    @media (max-width: 768px) {
        margin-bottom: 30px;
        width: 100%; // Stack columns on mobile
        flex: none; // Reset flex properties for column layout
    }
`;

const FooterHeading = styled.div`
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 20px;
    position: relative;
    display: inline-block;
    color: var(--text-color); // Ensure heading color
    font-weight: 700; // Make it bold like a heading

    &:after {
        content: "";
        position: absolute;
        bottom: -5px;
        left: 0;
        width: 30px;
        height: 2px;
        background-color: var(--primary-color);
    }
`;

const FooterLinks = styled.ul`
    list-style: none;
    padding: 0; // Remove default padding
`;

const FooterLink = styled.li`
    margin-bottom: 10px;
`;

// Apply the imported shared style
const StyledLink = styled(Link)`
    ${bracketLinkHoverStyle}
        /* Add any specific overrides for Footer's StyledLink AFTER the import */
`;

// Apply the imported shared style
const ExternalLink = styled.a`
    ${bracketLinkHoverStyle}
    display: inline-flex;
    align-items: center;
        /* Add any specific overrides for Footer's ExternalLink AFTER the import */
`;

const Copyright = styled.div`
    margin-top: 40px; /* Reduced margin a bit */
    text-align: center;
    font-size: 12px;
    color: #888888; /* Improved contrast while keeping faded grey aesthetic */
    width: 100%;

    p {
        margin-bottom: 10px; /* Space between copyright and policy link */
    }

    a { /* Style the privacy link specifically if needed */
        color: #888888; /* Same color for consistency */
        text-decoration: underline;
        text-underline-offset: 2px;
        &:hover {
            color: var(--primary-color);
        }
    }
`;

// MODIFIED: Create a wrapper for legal links for better spacing
const FooterLegalLinks = styled.div`
    display: flex;
    justify-content: center;
    gap: 20px; /* Space between links */
    align-items: center;
`;

const Icon = styled.img`
    margin-right: 8px;
    width: ${props => props.$size || '16px'};
    height: ${props => props.$size || '16px'};
    transition: filter 0.3s ease;
    /* Default: match the unhovered text color (--text-muted) */
    filter: invert(70%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(90%) contrast(90%);

    /* On parent hover: match the primary color (#ff3399) */
    ${ExternalLink}:hover & {
         filter: invert(36%) sepia(63%) saturate(3061%) hue-rotate(309deg) brightness(102%) contrast(101%);
    }
`;

const Footer = () => {
    const currentYear = new Date().getFullYear();
    return (
        <FooterWrapper>
            <PageContainer>
                <FooterContent>
                    {/* Column 1 */}
                    <FooterColumn>
                        <FooterHeading>Navigation</FooterHeading>
                        <FooterLinks>
                            <FooterLink><StyledLink to="/">Home</StyledLink></FooterLink>
                            <FooterLink><StyledLink to="/projects">Projects</StyledLink></FooterLink>
                            <FooterLink><StyledLink to="/about">About</StyledLink></FooterLink>
                            <FooterLink><StyledLink to="/contact">Contact</StyledLink></FooterLink>
                        </FooterLinks>
                    </FooterColumn>

                    {/* Column 2 - Updated with query parameter links */}
                    <FooterColumn>
                        <FooterHeading>Categories</FooterHeading>
                        <FooterLinks>
                            <FooterLink><StyledLink to="/projects?category=audio software">Audio Software</StyledLink></FooterLink>
                            <FooterLink><StyledLink to="/projects?category=gaming">Games</StyledLink></FooterLink>
                            <FooterLink><StyledLink to="/projects?category=productivity">Productivity</StyledLink></FooterLink>
                            <FooterLink><StyledLink to="/projects?category=other">Other</StyledLink></FooterLink>
                        </FooterLinks>
                    </FooterColumn>

                    {/* Column 3 */}
                    <FooterColumn>
                        <FooterHeading>Contact</FooterHeading>
                        <FooterLinks>
                            <FooterLink><ExternalLink href={`mailto:${email}`}><Icon src="/assets/icons/mail.svg" alt="Email" $size="12.8px" />{email}</ExternalLink></FooterLink>
                            <FooterLink><ExternalLink href={gitlab} target="_blank" rel="noopener noreferrer"><Icon src="/assets/icons/gitlab.svg" alt="GitLab" $size="12.8px" />GitLab</ExternalLink></FooterLink>
                        </FooterLinks>
                    </FooterColumn>
                </FooterContent>

                <Copyright>
                    <p>© {currentYear} Lucas Perron // ALL RIGHTS RESERVED</p>
                    {/* MODIFIED: Use the new wrapper for legal links */}
                    <FooterLegalLinks>
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <span aria-hidden="true">|</span>
                        <Link to="/terms-of-use">Terms of Use</Link>
                    </FooterLegalLinks>
                </Copyright>
            </PageContainer>
        </FooterWrapper>
    );
};

export default Footer;